import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStoreAuth } from '../../contexts/StoreAuthContext';
import StoreHeader from '../../components/Layout/StoreHeader';
import { customersAPI, feedFormulasAPI, salesOrdersAPI, usersAPI } from '../../services/api';
import CustomerCreationModal from '../../components/CustomerCreationModal';
import { getPacificToday } from '../../utils/dateUtils';
import '../../pages/OrderEntry.css';

interface Customer {
  id: number;
  name: string;
  customer_type: string;
  city: string;
  state: string;
  postal_code: string;
  address: string;
  phone: string;
  email: string;
  is_tax_exempt?: boolean;
}

interface User {
  id: number;
  username: string;
  first_name: string;
  role: string;
}

interface FeedFormula {
  id: number;
  name: string;
  description: string;
  feed_grade: string;
  animal_type: string;
  bag_size: number;
  price_bag_mash: number;
  price_bag_pellet: number;
  price_byoc_mash: number;
  price_byoc_pellet: number;
  price_1000lb_mash: number;
  price_1000lb_pellet: number;
  price_2000lb_mash: number;
  price_2000lb_pellet: number;
  pellets_available: boolean;
  default_form: string;
  available_forms: string[];
}

interface OrderLineItem {
  id: string;
  formula: FeedFormula | null;
  selectedGrade: string;
  selectedAnimal: string;
  isPellet: boolean;
  totalPounds: number;
  numberOfPackages: number;
  packageSizeLbs: number;
  calculatedPrice?: {
    item_total: number;
    price_per_lb?: number;
    price_per_package?: number;
    pricing_display_method?: string;
    unit_price?: number;
  };
}

// THE package_type derivation for a line item — used by BOTH the live-price
// preview and the save payload so the two cannot disagree by construction.
// (They used to: the preview derived a type from the calculated bag size while
// save sent none at all, so the backend defaulted it to 40# BAGS and rewrote
// the weight — order 2608-0010, 2 x 500# totes saved as 2 x 40# bags.)
// Returns the lowercase token /sales-orders/calculate_price/ matches on; the
// save payload uppercases it to match PACKAGE_TYPE_CHOICES (the server's
// PKG_MAP maps SUPERSACK -> SUPERSACK_2600).
const derivePackageType = (packageSizeLbs: number): string => {
  if (packageSizeLbs === 40) return 'bags';
  if (packageSizeLbs === 19) return 'bags_19';
  if (packageSizeLbs > 0 && packageSizeLbs < 1000) return 'bulk';
  if (packageSizeLbs >= 1000 && packageSizeLbs <= 2000) return 'supersack';
  return 'bulk';
};

const SHIPPING_METHODS = [
  { value: 'STORE', label: 'In-Store Sale' },
  { value: 'WILL_CALL_KF', label: 'Will Call - Kettle Falls' },
  { value: 'WILL_CALL_FW', label: 'Will Call - Fastway/Peninsula' },
  { value: 'LTL_DOCK', label: 'LTL Ship to Dock', isLTL: true },
  { value: 'LTL_BUSINESS', label: 'LTL Ship to Business', isLTL: true },
  { value: 'MILK_RUN', label: 'Milk Run' },
  { value: 'FASTWAY_FREIGHT', label: 'Fastway/Peninsula Freight' },
];

const StoreOrderEntry: React.FC = () => {
  const { user, isAuthenticated } = useStoreAuth();
  const navigate = useNavigate();

  // State
  const [customerResults, setCustomerResults] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const customerSearchRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [employees, setEmployees] = useState<User[]>([]);
  const [enteredBy, setEnteredBy] = useState<number | null>(null);

  const [deliveryDate, setDeliveryDate] = useState('');
  const [shippingMethod, setShippingMethod] = useState('STORE');
  const [isTaxExempt, setIsTaxExempt] = useState(false);
  const [isRush, setIsRush] = useState(false);
  const [needsLiftGate, setNeedsLiftGate] = useState(false);
  const [specialInstructions, setSpecialInstructions] = useState('');

  const [lineItems, setLineItems] = useState<OrderLineItem[]>([
    {
      id: crypto.randomUUID(),
      formula: null,
      selectedGrade: '',
      selectedAnimal: '',
      isPellet: false,
      totalPounds: 0,
      numberOfPackages: 1,
      packageSizeLbs: 0,
    },
  ]);

  const [feedGrades, setFeedGrades] = useState<Array<{ value: string; label: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Ref to track last pricing request to avoid duplicate API calls
  const lastPriceRequestRef = useRef<string>('');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/store/login');
      return;
    }
    loadFeedGrades();
    loadEmployees();
  }, [isAuthenticated, navigate]);

  // Set tax exempt when customer changes
  useEffect(() => {
    if (selectedCustomer) {
      setIsTaxExempt(selectedCustomer.is_tax_exempt || false);
    }
  }, [selectedCustomer]);

  // Debounced pricing recalculation
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      calculatePricing();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [selectedCustomer, lineItems]);

  const loadEmployees = async () => {
    try {
      const response = await usersAPI.getEmployees();
      setEmployees(response.data);
      // Set default to current logged-in store user
      if (user?.id) {
        setEnteredBy(user.id);
      }
    } catch (err) {
      console.error('Error loading employees:', err);
    }
  };

  const searchCustomers = useCallback(async (query: string) => {
    if (query.length < 2) {
      setCustomerResults([]);
      return;
    }
    setCustomerSearchLoading(true);
    try {
      const response = await customersAPI.getAll({ search: query, page_size: 20, is_active: true });
      const customerData = response.data.results || response.data;
      setCustomerResults(customerData.filter((c: Customer) => c.id));
    } catch (err) {
      console.error('Error searching customers:', err);
    } finally {
      setCustomerSearchLoading(false);
    }
  }, []);

  const loadFeedGrades = async () => {
    try {
      const response = await feedFormulasAPI.getFilterOptions({});
      const grades = response.data.grades || [];
      const gradeOrder = ['ESSENTIAL', 'PLUS', 'FIVE_STAR'];
      const sortedGrades = grades.sort((a: any, b: any) => {
        const aIndex = gradeOrder.indexOf(a.value);
        const bIndex = gradeOrder.indexOf(b.value);
        return aIndex - bIndex;
      });
      setFeedGrades(sortedGrades);
    } catch (err) {
      console.error('Error loading feed grades:', err);
    }
  };

  const loadAnimalTypes = async (lineItemId: string, grade: string) => {
    try {
      const response = await feedFormulasAPI.getFilterOptions({ feed_grade: grade });
      return response.data.animal_types || [];
    } catch (err) {
      console.error('Error loading animal types:', err);
      return [];
    }
  };

  const loadFormulas = async (lineItemId: string, grade: string, animal: string) => {
    try {
      const response = await feedFormulasAPI.getFilterOptions({
        feed_grade: grade,
        animal_type: animal,
      });
      return response.data.formulas || [];
    } catch (err) {
      console.error('Error loading formulas:', err);
      return [];
    }
  };

  const handleGradeChange = (lineItemId: string, grade: string) => {
    setLineItems((prev) =>
      prev.map((item) =>
        item.id === lineItemId
          ? { ...item, selectedGrade: grade, selectedAnimal: '', formula: null, isPellet: false }
          : item
      )
    );
  };

  const handleAnimalChange = (lineItemId: string, animal: string) => {
    setLineItems((prev) =>
      prev.map((item) =>
        item.id === lineItemId
          ? { ...item, selectedAnimal: animal, formula: null, isPellet: false }
          : item
      )
    );
  };

  const handleFormulaChange = (lineItemId: string, formulaId: number, formulas: FeedFormula[]) => {
    const formula = formulas.find((f) => f.id === formulaId) || null;
    let defaultIsPellet = false;
    if (formula && formula.default_form) {
      defaultIsPellet = formula.default_form === 'PELLET';
    }
    setLineItems((prev) =>
      prev.map((item) =>
        item.id === lineItemId ? { ...item, formula, isPellet: defaultIsPellet } : item
      )
    );
  };

  const handlePelletToggle = (lineItemId: string, isPellet: boolean) => {
    setLineItems((prev) =>
      prev.map((item) => (item.id === lineItemId ? { ...item, isPellet } : item))
    );
  };

  const handleTotalPoundsChange = (lineItemId: string, totalPounds: number) => {
    setLineItems((prev) =>
      prev.map((item) => (item.id === lineItemId ? { ...item, totalPounds } : item))
    );
  };

  const handleTotalPoundsBlur = (lineItemId: string) => {
    setLineItems((prev) =>
      prev.map((item) => {
        if (item.id === lineItemId) {
          if (item.numberOfPackages <= 0) return { ...item, packageSizeLbs: 0 };
          const calculatedSize = item.totalPounds / item.numberOfPackages;
          let packageSizeLbs = calculatedSize;
          let adjustedTotalPounds = item.totalPounds;
          if (calculatedSize >= 38 && calculatedSize <= 42) {
            packageSizeLbs = 40;
            adjustedTotalPounds = item.numberOfPackages * 40;
          } else if (calculatedSize >= 17 && calculatedSize <= 21) {
            packageSizeLbs = 19;
            adjustedTotalPounds = item.numberOfPackages * 19;
          } else {
            packageSizeLbs = Math.ceil(calculatedSize);
          }
          return { ...item, totalPounds: adjustedTotalPounds, packageSizeLbs };
        }
        return item;
      })
    );
  };

  const handleNumberOfPackagesChange = (lineItemId: string, numberOfPackages: number) => {
    setLineItems((prev) =>
      prev.map((item) => (item.id === lineItemId ? { ...item, numberOfPackages } : item))
    );
  };

  const handleNumberOfPackagesBlur = (lineItemId: string) => {
    setLineItems((prev) =>
      prev.map((item) => {
        if (item.id === lineItemId) {
          if (item.numberOfPackages <= 0 || item.totalPounds <= 0) return { ...item, packageSizeLbs: 0 };
          const calculatedSize = item.totalPounds / item.numberOfPackages;
          let packageSizeLbs = calculatedSize;
          let adjustedTotalPounds = item.totalPounds;
          if (calculatedSize >= 38 && calculatedSize <= 42) {
            packageSizeLbs = 40;
            adjustedTotalPounds = item.numberOfPackages * 40;
          } else if (calculatedSize >= 17 && calculatedSize <= 21) {
            packageSizeLbs = 19;
            adjustedTotalPounds = item.numberOfPackages * 19;
          } else {
            packageSizeLbs = Math.ceil(calculatedSize);
          }
          return { ...item, totalPounds: adjustedTotalPounds, packageSizeLbs };
        }
        return item;
      })
    );
  };

  const calculatePricing = useCallback(async () => {
    if (!selectedCustomer) return;
    const validItems = lineItems.filter((item) => item.formula && item.totalPounds > 0);
    if (validItems.length === 0) {
      setLineItems((prev) => prev.map((item) => ({ ...item, calculatedPrice: undefined })));
      lastPriceRequestRef.current = '';
      return;
    }

    // Create request key to avoid duplicate API calls
    const requestKey = `${selectedCustomer.id}|` + validItems.map((item) =>
      `${item.formula!.id}-${item.isPellet}-${item.totalPounds}-${item.numberOfPackages}-${item.packageSizeLbs}`
    ).join('|');

    if (requestKey === lastPriceRequestRef.current) {
      return; // Skip - same request as last time
    }
    lastPriceRequestRef.current = requestKey;

    try {
      const payload = {
        customer_id: selectedCustomer.id,
        items: validItems.map((item) => ({
          formula_id: item.formula!.id,
          is_pellet: item.isPellet,
          total_quantity_lbs: item.totalPounds,
          number_of_packages: item.numberOfPackages,
          package_type: derivePackageType(item.packageSizeLbs),
        })),
      };
      const response = await salesOrdersAPI.calculatePrice(payload);
      const pricedItems = response.data.items;
      setLineItems((prev) =>
        prev.map((item) => {
          const pricedItem = pricedItems.find((p: any) => p.formula_id === item.formula?.id);
          if (pricedItem) {
            return {
              ...item,
              calculatedPrice: {
                item_total: pricedItem.item_total,
                price_per_lb: pricedItem.price_per_lb,
                price_per_package: pricedItem.price_per_package,
                pricing_display_method: pricedItem.pricing_display_method,
                unit_price: pricedItem.unit_price,
              },
            };
          }
          return { ...item, calculatedPrice: undefined };
        })
      );
    } catch (err) {
      console.error('Error calculating pricing:', err);
    }
  }, [selectedCustomer, lineItems]);

  const addLineItem = () => {
    setLineItems((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        formula: null,
        selectedGrade: '',
        selectedAnimal: '',
        isPellet: false,
        totalPounds: 0,
        numberOfPackages: 1,
        packageSizeLbs: 0,
      },
    ]);
  };

  const removeLineItem = (lineItemId: string) => {
    if (lineItems.length === 1) return;
    setLineItems((prev) => prev.filter((item) => item.id !== lineItemId));
  };

  const calculateOrderTotals = () => {
    let totalWeight = 0;
    let subtotal = 0;
    lineItems.forEach((item) => {
      if (!item.formula || item.totalPounds <= 0) return;
      totalWeight += item.totalPounds;
      if (item.calculatedPrice) subtotal += item.calculatedPrice.item_total;
    });

    const estimatedPallets = totalWeight > 0 ? Math.ceil(totalWeight / 2600) : 0;
    const totalBags = lineItems.reduce((sum, item) => sum + item.numberOfPackages, 0);

    // Shipping estimate
    let shippingEstimate = 0;
    const isLTL = SHIPPING_METHODS.find(m => m.value === shippingMethod)?.isLTL || false;
    const isRetailPartner = selectedCustomer?.customer_type === 'RETAIL_PARTNER';

    if (isLTL) {
      if (isRetailPartner && totalBags >= 50) {
        shippingEstimate = 0; // Free shipping for retail partners 50+ bags
      } else {
        shippingEstimate = 150; // First pallet
        if (estimatedPallets > 1) {
          shippingEstimate += (estimatedPallets - 1) * 75; // Additional pallets
        }
      }
    }

    const liftGateFee = needsLiftGate && isLTL ? 50 : 0;
    const taxRate = 0.08; // 8.0% Stevens County, WA
    const taxAmount = isTaxExempt ? 0 : subtotal * taxRate;
    const total = subtotal + taxAmount + shippingEstimate + liftGateFee;

    return {
      totalWeight,
      totalBags,
      subtotal,
      taxRate: taxRate * 100,
      taxAmount,
      shippingEstimate,
      liftGateFee,
      total,
      estimatedPallets,
      isLTL,
      isRetailPartner,
    };
  };

  const filteredCustomers = customerResults;

  const handleSelectCustomer = (customer: Customer) => {
    setSelectedCustomer(customer);
    setCustomerSearch('');
    setShowCustomerDropdown(false);
  };

  const handleSubmitOrder = async () => {
    setError('');
    setSuccess('');

    if (!selectedCustomer) {
      setError('Please select a customer');
      return;
    }
    if (!deliveryDate) {
      setError('Please select a delivery date');
      return;
    }
    const validLineItems = lineItems.filter((item) => item.formula && item.totalPounds > 0);
    if (validLineItems.length === 0) {
      setError('Please add at least one valid feed line item');
      return;
    }

    setLoading(true);
    try {
      const orderData = {
        customer: selectedCustomer.id,
        order_date: getPacificToday(),
        delivery_date: deliveryDate,
        shipping_method: shippingMethod,
        is_rush: isRush,
        is_tax_exempt: isTaxExempt,
        special_instructions: specialInstructions,
        status: 'PENDING',
        entered_by: enteredBy || user?.id,
        entered_at: new Date().toISOString(),
        entry_source: 'store', // Tag as Store Portal order
        items: validLineItems.map((item) => ({
          formula: item.formula!.id,
          is_pellet: item.isPellet,
          total_quantity_lbs: item.totalPounds,
          number_of_packages: item.numberOfPackages,
          // Same derivation the preview uses, uppercased for
          // PACKAGE_TYPE_CHOICES. Sending this is what stops the server
          // defaulting to 40# BAGS and rewriting the entered weight.
          package_type: derivePackageType(item.packageSizeLbs).toUpperCase(),
          // Priced-as hint from the preview response, mirroring
          // PortalPlaceOrder.tsx:778.
          pricing_display_method: item.calculatedPrice?.pricing_display_method || 'PER_LB',
        })),
      };

      const response = await salesOrdersAPI.create(orderData);
      setSuccess(`Order #${response.data.order_number || 'NEW'} created successfully!`);

      // Reset form
      setLineItems([{
        id: crypto.randomUUID(),
        formula: null,
        selectedGrade: '',
        selectedAnimal: '',
        isPellet: false,
        totalPounds: 0,
        numberOfPackages: 1,
        packageSizeLbs: 0,
      }]);
      setSelectedCustomer(null);
      setDeliveryDate('');
      setShippingMethod('STORE');
      setIsTaxExempt(false);
      setIsRush(false);
      setNeedsLiftGate(false);
      setSpecialInstructions('');
    } catch (err: any) {
      console.error('Error creating order:', err);
      setError(err.response?.data?.message || err.response?.data?.detail || 'Failed to create order');
    } finally {
      setLoading(false);
    }
  };

  const totals = calculateOrderTotals();

  if (!isAuthenticated) return null;

  return (
    <div className="order-entry-page">
      <StoreHeader />

      <div className="page-header">
        <h1>Feed Order Entry</h1>
        <p className="page-subtitle">Red Bridge Farm Feed Co. - Store Portal</p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert" style={{ backgroundColor: '#d4edda', border: '1px solid #c3e6cb', color: '#155724' }}>{success}</div>}

      <div className="order-entry-container">
        {/* LEFT COLUMN - Order Form */}
        <div className="order-form-column">
          <div className="order-form">
            {/* Customer Selection */}
            <div className="form-section">
              <h3 className="section-title">Customer Information</h3>
              <div className="customer-select-row">
                <div style={{ flex: 1, position: 'relative' }}>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Search customer by name, city, or phone..."
                    value={selectedCustomer ? selectedCustomer.name : customerSearch}
                    onChange={(e) => {
                      const val = e.target.value;
                      setCustomerSearch(val);
                      setShowCustomerDropdown(true);
                      if (!val) {
                        setSelectedCustomer(null);
                        setCustomerResults([]);
                      }
                      if (customerSearchRef.current) clearTimeout(customerSearchRef.current);
                      customerSearchRef.current = setTimeout(() => searchCustomers(val), 300);
                    }}
                    onFocus={() => {
                      if (!selectedCustomer) setShowCustomerDropdown(true);
                    }}
                    onClick={() => {
                      if (selectedCustomer) {
                        setSelectedCustomer(null);
                        setCustomerSearch('');
                        setShowCustomerDropdown(true);
                      }
                    }}
                  />
                  {showCustomerDropdown && customerSearch && !selectedCustomer && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: '#fff',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      zIndex: 100,
                      maxHeight: '300px',
                      overflowY: 'auto',
                    }}>
                      {customerSearchLoading ? (
                        <div style={{ padding: '0.75rem 1rem', color: '#999' }}>Searching...</div>
                      ) : customerSearch.length < 2 ? (
                        <div style={{ padding: '0.75rem 1rem', color: '#999' }}>Type at least 2 characters...</div>
                      ) : filteredCustomers.length === 0 ? (
                        <div style={{ padding: '0.75rem 1rem', color: '#999' }}>No customers found</div>
                      ) : (
                        filteredCustomers.map(customer => (
                          <div
                            key={customer.id}
                            style={{
                              padding: '0.75rem 1rem',
                              cursor: 'pointer',
                              borderBottom: '1px solid #f0f0f0',
                            }}
                            onClick={() => handleSelectCustomer(customer)}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <div style={{ fontWeight: 600, color: '#333' }}>{customer.name}</div>
                            <div style={{ fontSize: '0.85rem', color: '#666' }}>
                              {customer.city}, {customer.state} {customer.phone && `| ${customer.phone}`}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
                <button className="btn btn-secondary btn-sm" onClick={() => setShowCustomerModal(true)}>
                  + New Customer
                </button>
              </div>

              {selectedCustomer && (
                <div className="customer-info">
                  <div className="customer-info-header">
                    <h4>{selectedCustomer.name}</h4>
                    <span className="customer-type-badge">
                      {selectedCustomer.customer_type.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <div className="customer-info-section">
                    <strong>Address:</strong>
                    <div className="customer-address">
                      {selectedCustomer.address && <div>{selectedCustomer.address}</div>}
                      <div>
                        {selectedCustomer.city}
                        {selectedCustomer.city && selectedCustomer.state && ', '}
                        {selectedCustomer.state} {selectedCustomer.postal_code}
                      </div>
                    </div>
                  </div>
                  <div className="customer-info-section">
                    {selectedCustomer.phone && <p><strong>Phone:</strong> {selectedCustomer.phone}</p>}
                    {selectedCustomer.email && <p><strong>Email:</strong> {selectedCustomer.email}</p>}
                  </div>
                </div>
              )}
            </div>

            {/* Delivery & Shipping */}
            <div className="form-section">
              <h3 className="section-title">Delivery & Shipping</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Delivery Date *</label>
                  <input
                    type="date"
                    className="form-input"
                    value={deliveryDate}
                    onChange={(e) => setDeliveryDate(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Shipping Method *</label>
                  <select
                    className="form-select"
                    value={shippingMethod}
                    onChange={(e) => setShippingMethod(e.target.value)}
                  >
                    {SHIPPING_METHODS.map((method) => (
                      <option key={method.value} value={method.value}>
                        {method.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Entered By *</label>
                  <select
                    className="form-select"
                    value={enteredBy || ''}
                    onChange={(e) => setEnteredBy(Number(e.target.value))}
                  >
                    <option value="">Select Employee</option>
                    {employees.map((employee) => (
                      <option key={employee.id} value={employee.id}>
                        {employee.first_name || employee.username}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row" style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={isTaxExempt}
                    onChange={(e) => setIsTaxExempt(e.target.checked)}
                  />
                  Tax Exempt
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={isRush}
                    onChange={(e) => setIsRush(e.target.checked)}
                  />
                  Rush Order
                </label>
                {totals.isLTL && (
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={needsLiftGate}
                      onChange={(e) => setNeedsLiftGate(e.target.checked)}
                    />
                    Lift Gate Needed (+$50)
                  </label>
                )}
              </div>
            </div>

            {/* Feed Line Items */}
            <div className="form-section">
              <div className="section-header-row">
                <h3 className="section-title">Feed Line Items</h3>
                <button className="btn btn-secondary btn-sm" onClick={addLineItem}>
                  + Add Feed Type
                </button>
              </div>

              {lineItems.map((lineItem, index) => (
                <LineItemForm
                  key={lineItem.id}
                  lineItem={lineItem}
                  index={index}
                  feedGrades={feedGrades}
                  onGradeChange={handleGradeChange}
                  onAnimalChange={handleAnimalChange}
                  onFormulaChange={handleFormulaChange}
                  onPelletToggle={handlePelletToggle}
                  onTotalPoundsChange={handleTotalPoundsChange}
                  onTotalPoundsBlur={handleTotalPoundsBlur}
                  onNumberOfPackagesChange={handleNumberOfPackagesChange}
                  onNumberOfPackagesBlur={handleNumberOfPackagesBlur}
                  onRemove={removeLineItem}
                  canRemove={lineItems.length > 1}
                  loadAnimalTypes={loadAnimalTypes}
                  loadFormulas={loadFormulas}
                />
              ))}
            </div>

            {/* Special Instructions */}
            <div className="form-section">
              <h3 className="section-title">Order Notes</h3>
              <textarea
                className="form-textarea"
                rows={3}
                placeholder="Any special delivery or handling instructions..."
                value={specialInstructions}
                onChange={(e) => setSpecialInstructions(e.target.value)}
              ></textarea>
            </div>

            {/* Form Actions */}
            <div className="form-actions">
              <button className="btn btn-secondary" onClick={() => navigate('/store/dashboard')}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSubmitOrder}
                disabled={loading}
              >
                {loading ? 'Creating Order...' : 'Submit Order'}
              </button>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN - Order Summary */}
        <div className="order-summary-column">
          <OrderSummary
            selectedCustomer={selectedCustomer}
            lineItems={lineItems}
            totals={totals}
            isTaxExempt={isTaxExempt}
            shippingMethod={shippingMethod}
            needsLiftGate={needsLiftGate}
            employees={employees}
            enteredBy={enteredBy}
            deliveryDate={deliveryDate}
            isRush={isRush}
          />
        </div>
      </div>

      {/* Customer Creation Modal */}
      <CustomerCreationModal
        isOpen={showCustomerModal}
        onClose={() => setShowCustomerModal(false)}
        onCustomerCreated={(newCustomer) => {
          setSelectedCustomer(newCustomer);
          setCustomerSearch('');
          setShowCustomerModal(false);
        }}
      />
    </div>
  );
};

// Line Item Form Component
interface LineItemFormProps {
  lineItem: OrderLineItem;
  index: number;
  feedGrades: Array<{ value: string; label: string }>;
  onGradeChange: (id: string, grade: string) => void;
  onAnimalChange: (id: string, animal: string) => void;
  onFormulaChange: (id: string, formulaId: number, formulas: FeedFormula[]) => void;
  onPelletToggle: (id: string, isPellet: boolean) => void;
  onTotalPoundsChange: (id: string, totalPounds: number) => void;
  onTotalPoundsBlur: (id: string) => void;
  onNumberOfPackagesChange: (id: string, numberOfPackages: number) => void;
  onNumberOfPackagesBlur: (id: string) => void;
  onRemove: (id: string) => void;
  canRemove: boolean;
  loadAnimalTypes: (id: string, grade: string) => Promise<Array<{ value: string; label: string }>>;
  loadFormulas: (id: string, grade: string, animal: string) => Promise<FeedFormula[]>;
}

const LineItemForm: React.FC<LineItemFormProps> = ({
  lineItem,
  index,
  feedGrades,
  onGradeChange,
  onAnimalChange,
  onFormulaChange,
  onPelletToggle,
  onTotalPoundsChange,
  onTotalPoundsBlur,
  onNumberOfPackagesChange,
  onNumberOfPackagesBlur,
  onRemove,
  canRemove,
  loadAnimalTypes,
  loadFormulas,
}) => {
  const [animalTypes, setAnimalTypes] = useState<Array<{ value: string; label: string }>>([]);
  const [formulas, setFormulas] = useState<FeedFormula[]>([]);

  useEffect(() => {
    if (lineItem.selectedGrade) {
      loadAnimalTypes(lineItem.id, lineItem.selectedGrade).then(setAnimalTypes);
    } else {
      setAnimalTypes([]);
    }
  }, [lineItem.selectedGrade, lineItem.id]);

  useEffect(() => {
    if (lineItem.selectedGrade && lineItem.selectedAnimal) {
      loadFormulas(lineItem.id, lineItem.selectedGrade, lineItem.selectedAnimal).then(setFormulas);
    } else {
      setFormulas([]);
    }
  }, [lineItem.selectedGrade, lineItem.selectedAnimal, lineItem.id]);

  const availableForms = lineItem.formula?.available_forms || [];
  const isMashAvailable = availableForms.includes('MASH');
  const isPelletAvailable = availableForms.includes('PELLET');

  return (
    <div className="line-item-card">
      <div className="line-item-header">
        <h4>Feed Type #{index + 1}</h4>
        {canRemove && (
          <button className="btn-remove" onClick={() => onRemove(lineItem.id)}>
            X Remove
          </button>
        )}
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Feed Grade *</label>
          <select
            className="form-select"
            value={lineItem.selectedGrade}
            onChange={(e) => onGradeChange(lineItem.id, e.target.value)}
          >
            <option value="">Select Grade...</option>
            {feedGrades.map((grade) => (
              <option key={grade.value} value={grade.value}>
                {grade.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Animal Type *</label>
          <select
            className="form-select"
            value={lineItem.selectedAnimal}
            onChange={(e) => onAnimalChange(lineItem.id, e.target.value)}
            disabled={!lineItem.selectedGrade}
          >
            <option value="">Select Animal...</option>
            {animalTypes.map((animal) => (
              <option key={animal.value} value={animal.value}>
                {animal.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="form-group">
        <label>Formula *</label>
        <select
          className="form-select"
          value={lineItem.formula?.id || ''}
          onChange={(e) => onFormulaChange(lineItem.id, Number(e.target.value), formulas)}
          disabled={!lineItem.selectedAnimal}
        >
          <option value="">Select Formula...</option>
          {formulas.map((formula) => (
            <option key={formula.id} value={formula.id}>
              {formula.name}
            </option>
          ))}
        </select>
      </div>

      {lineItem.formula && (
        <>
          <div className="form-group">
            <label>Form</label>
            <div className="radio-group">
              <label className="radio-label">
                <input
                  type="radio"
                  name={`form-${lineItem.id}`}
                  checked={!lineItem.isPellet}
                  onChange={() => onPelletToggle(lineItem.id, false)}
                  disabled={!isMashAvailable}
                />
                Mash {!isMashAvailable && '(N/A)'}
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name={`form-${lineItem.id}`}
                  checked={lineItem.isPellet}
                  onChange={() => onPelletToggle(lineItem.id, true)}
                  disabled={!isPelletAvailable}
                />
                Pellet {!isPelletAvailable && '(N/A)'}
              </label>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Total Pounds *</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                className="form-input"
                placeholder="Enter weight"
                value={lineItem.totalPounds || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || /^\d+$/.test(value)) {
                    onTotalPoundsChange(lineItem.id, value === '' ? 0 : Number(value));
                  }
                }}
                onBlur={() => onTotalPoundsBlur(lineItem.id)}
                onFocus={(e) => e.target.select()}
              />
            </div>
            <div className="form-group">
              <label># of Bags *</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                className="form-input"
                placeholder="Enter quantity"
                value={lineItem.numberOfPackages || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || /^\d+$/.test(value)) {
                    onNumberOfPackagesChange(lineItem.id, value === '' ? 0 : Math.max(1, Number(value)));
                  }
                }}
                onBlur={() => onNumberOfPackagesBlur(lineItem.id)}
                onFocus={(e) => e.target.select()}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Calculated Bag Size</label>
            <div className="calculated-value">
              {lineItem.packageSizeLbs > 0 ? `${lineItem.packageSizeLbs} lbs per bag` : 'Enter total pounds and # of bags'}
            </div>
          </div>

          {lineItem.calculatedPrice && (
            <div style={{
              marginTop: '0.75rem',
              padding: '0.75rem',
              backgroundColor: '#e8f5e9',
              borderRadius: '6px',
              border: '1px solid #4caf50',
            }}>
              <strong style={{ color: '#2e7d32' }}>
                Line Total: ${lineItem.calculatedPrice.item_total.toFixed(2)}
              </strong>
              <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                {lineItem.calculatedPrice.pricing_display_method === 'PER_PACKAGE' && lineItem.calculatedPrice.price_per_package
                  ? `$${lineItem.calculatedPrice.price_per_package.toFixed(2)}/bag x ${lineItem.numberOfPackages} bags`
                  : `$${(lineItem.calculatedPrice.price_per_lb || 0).toFixed(4)}/lb x ${lineItem.totalPounds} lbs`
                }
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Order Summary Component
interface OrderSummaryProps {
  selectedCustomer: Customer | null;
  lineItems: OrderLineItem[];
  totals: {
    totalWeight: number;
    totalBags: number;
    subtotal: number;
    taxRate: number;
    taxAmount: number;
    shippingEstimate: number;
    liftGateFee: number;
    total: number;
    estimatedPallets: number;
    isLTL: boolean;
    isRetailPartner: boolean;
  };
  isTaxExempt: boolean;
  shippingMethod: string;
  needsLiftGate: boolean;
  employees: User[];
  enteredBy: number | null;
  deliveryDate: string;
  isRush: boolean;
}

const OrderSummary: React.FC<OrderSummaryProps> = ({
  selectedCustomer,
  lineItems,
  totals,
  isTaxExempt,
  shippingMethod,
  needsLiftGate,
  employees,
  enteredBy,
  deliveryDate,
  isRush,
}) => {
  const validLineItems = lineItems.filter((item) => item.formula && item.totalPounds > 0);

  if (!selectedCustomer || validLineItems.length === 0) {
    return (
      <div className="order-summary-card">
        <h3 className="summary-title">Order Summary</h3>
        <div className="summary-empty">Select customer and add feed items to see order summary</div>
      </div>
    );
  }

  return (
    <div className="order-summary-card">
      <h3 className="summary-title">Order Summary</h3>

      <div className="summary-section">
        <p><strong>Customer:</strong> {selectedCustomer.name}</p>
        <p><strong>Type:</strong> {selectedCustomer.customer_type.replace(/_/g, ' ')}</p>
        <p><strong>Shipping:</strong> {SHIPPING_METHODS.find((m) => m.value === shippingMethod)?.label}</p>
        {deliveryDate && <p><strong>Delivery:</strong> {new Date(deliveryDate).toLocaleDateString()}</p>}
        {isRush && <p style={{ color: '#e73c3e', fontWeight: 600 }}>RUSH ORDER</p>}
        {enteredBy && (
          <p><strong>Entered By:</strong> {employees.find((e) => e.id === enteredBy)?.first_name || 'Unknown'}</p>
        )}
      </div>

      <div className="summary-divider"></div>

      <h4 className="materials-title">Feed Items ({validLineItems.length})</h4>
      <div className="materials-list">
        {validLineItems.map((item, index) => (
          <div key={item.id} className="material-item">
            <div className="material-name">
              {index + 1}. {item.formula!.name}
            </div>
            <div className="material-details">
              <p>{item.isPellet ? 'Pellet' : 'Mash'} | {item.totalPounds} lbs</p>
              <p>{item.numberOfPackages} bags x {item.packageSizeLbs} lbs</p>
              {item.calculatedPrice && (
                <p><strong>${item.calculatedPrice.item_total.toFixed(2)}</strong></p>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="summary-divider"></div>

      <div className="summary-pricing">
        <div className="price-row">
          <span>Total Weight:</span>
          <span>{totals.totalWeight.toLocaleString()} lbs</span>
        </div>
        <div className="price-row">
          <span>Total Bags:</span>
          <span>{totals.totalBags}</span>
        </div>
        <div className="price-row">
          <span>Estimated Pallets:</span>
          <span>{totals.estimatedPallets}</span>
        </div>
        <div className="price-row">
          <span>Subtotal:</span>
          <span>${totals.subtotal.toFixed(2)}</span>
        </div>

        {totals.isLTL && (
          <>
            <div className="price-row">
              <span>Est. Shipping*:</span>
              <span>
                {totals.isRetailPartner && totals.totalBags >= 50
                  ? 'FREE'
                  : `$${totals.shippingEstimate.toFixed(2)}`
                }
              </span>
            </div>
            {needsLiftGate && (
              <div className="price-row">
                <span>Lift Gate Fee:</span>
                <span>${totals.liftGateFee.toFixed(2)}</span>
              </div>
            )}
          </>
        )}

        <div className="price-row">
          <span>Tax ({isTaxExempt ? 'Exempt' : `${totals.taxRate}%`}):</span>
          <span>${totals.taxAmount.toFixed(2)}</span>
        </div>
        <div className="price-row total">
          <span>Total:</span>
          <span>${totals.total.toFixed(2)}</span>
        </div>
      </div>

      {totals.isLTL && (
        <div style={{
          fontSize: '0.75rem',
          color: '#666',
          marginTop: '0.5rem',
          fontStyle: 'italic',
        }}>
          *Shipping estimate. First pallet: $150, additional: $75 each.
          Retail Partners 50+ bags: FREE. Exact cost confirmed on invoice.
        </div>
      )}
    </div>
  );
};

export default StoreOrderEntry;
